/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useMouseOver } from "hooks/useMouseOver";
import { useDataTableContext } from "../store/DataTableProvider";
import { CellValueRendererBase } from "../cell-value-renderers/CellValueRendererBase";
import { Ast, AstNodeType, CellValue } from "@inscopix/ideas-hyperformula";
import { useState } from "react";
import { EuiFlexGroup, EuiFlexItem } from "@inscopix/ideas-eui";
import { MetadatumReferenceSelectorGdt } from "./MetadatumReferenceSelectorGdt";

interface UseMetadatumReferenceProps {
  tableId: string;
  rowId: string;
  formula: string;
  value: CellValue;
}

/**
 * Hook that calculates if the cell's formula encodes a current metadatum
 * reference.
 */
export const useMetadatumReference = ({
  tableId,
  rowId,
  formula,
  value,
}: UseMetadatumReferenceProps) => {
  const formulaToAst = useDataTableContext((s) => s.formulaToAst);
  const table = useDataTableContext((s) =>
    s.tables.find((t) => t.id === tableId),
  );
  let ast: Ast;

  try {
    ast = formulaToAst({ tableId, formula });
  } catch (error) {
    return undefined;
  }

  // Coerce the cell value to a string, number or boolean
  const coercedValue =
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
      ? value
      : null;

  if (
    table !== undefined &&
    ast.type === AstNodeType.FUNCTION_CALL &&
    ast.procedureName === "METADATUM" &&
    ast.args[0].type === AstNodeType.COLUMN_RANGE &&
    ast.args[1].type === AstNodeType.STRING
  ) {
    const columnIdx = ast.args[0].start.col;
    return {
      tableId,
      columnId: table.columns[columnIdx].id,
      rowId: rowId,
      metadataKey: ast.args[1].value,
      value: coercedValue,
    };
  }

  return undefined;
};

interface CellRendererMetadatumReferenceProps {
  eParentOfValue: HTMLElement;
  tableId: string;
  columnId: string;
  rowId: string;
  formula: string;
  value: CellValue;
  isCustomFormula: boolean;
}

/**
 * Cell renderer that renders cells in columns where the metadatum reference
 * selector is allowed
 */
export const CellRendererMetadatumReference = ({
  eParentOfValue,
  tableId,
  columnId,
  rowId,
  formula,
  value,
  isCustomFormula,
}: CellRendererMetadatumReferenceProps) => {
  const { isMouseOver } = useMouseOver(eParentOfValue);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const currentRef = useMetadatumReference({ tableId, rowId, formula, value });

  return (
    <EuiFlexGroup
      alignItems="center"
      justifyContent="spaceBetween"
      gutterSize="xs"
      responsive={false}
      css={css`
        width: 100%;
      `}
    >
      <EuiFlexItem grow={false}>
        <CellValueRendererBase
          value={value}
          isDefaultValue={!isCustomFormula}
        />
      </EuiFlexItem>{" "}
      {(isMouseOver || isPopoverOpen) && (
        <EuiFlexItem grow={false}>
          <MetadatumReferenceSelectorGdt
            isReadonly={false}
            rowId={rowId}
            currentReference={currentRef}
            tableId={tableId}
            columnId={columnId}
            onPopoverOpen={() => setIsPopoverOpen(true)}
            onPopoverClose={() => setIsPopoverOpen(false)}
          />
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );
};
